import { ErrorNotFound } from "../common/ErrorNotFound";
import { TappSDK } from "../core";
import { GetPools, GetPoolsParams, PoolInfo } from "../types/pool.type";
import {
  Nullable,
  PaginatedResult,
  RequestResult,
} from "../types/requestModule.type";

export default class PoolModule {
  protected _sdk: TappSDK;
  constructor(sdk: TappSDK) {
    this._sdk = sdk;
  }

  /**
   * Fetches a paginated list of liquidity pools from the public API.
   *
   * @param params - Optional parameters to control pagination and sorting.
   *   - `page`: The page number to fetch (defaults to 1).
   *   - `size`: Number of items per page (defaults to 10).
   *   - `sortBy`: Field to sort by (defaults to "tvl").
   *   - `type`: Optional pool type filter.
   *
   * @returns {Promise<PaginatedResult<GetPools>>} A Promise resolving to a paginated result of pools.
   */
  async getPools(params?: GetPoolsParams): Promise<PaginatedResult<GetPools>> {
    const res = await this._sdk.request.post<
      RequestResult<PaginatedResult<GetPools>>
    >("public/pool", {
      page: params?.page ?? 1,
      size: params?.size ?? 10,
      sortBy: params?.sortBy ?? "tvl",
      poolType: params?.type,
    });

    return res.result;
  }

  /**
   * 
   * Retrieves detailed information for a specific liquidity pool.
   *
   * @param poolId The unique identifier of the pool to retrieve.
   * @returns {Promise<PoolInfo>} A Promise that resolves to the pool information.
   * @throws ErrorNotFound if the pool does not exist or cannot be found.
   */
  async getInfo(poolId: string): Promise<PoolInfo> {
    const res = await this._sdk.request.post<RequestResult<Nullable<PoolInfo>>>(
      "public/pool_info",
      {
        poolId,
      }
    );

    if (!res.result) {
      throw new ErrorNotFound(`Pool not found. ${poolId}`);
    }

    return res.result;
  }
}
